// @ts-nocheck
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Copy, Check, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId: string;
  teamName: string;
  callerIsOwner: boolean;
  onMemberInvited: () => void;
}

const ownerRoles = [
  { value: 'team_manager', label: 'Manager' },
  { value: 'head_coach', label: 'Head Coach' },
  { value: 'assistant_coach', label: 'Assistant Coach' },
  { value: 'team_billing_contact', label: 'Billing Contact' },
  { value: 'viewer', label: 'Viewer' },
];

const managerRoles = [
  { value: 'team_manager', label: 'Manager' },
  { value: 'assistant_coach', label: 'Assistant Coach' },
  { value: 'viewer', label: 'Viewer' },
];

const roleDescriptions: Record<string, string> = {
  team_owner: 'Full control including transferring ownership. One per team.',
  team_billing_contact: 'Can view and pay invoices, but cannot edit roster or register for events.',
  team_manager: 'Can invite members, edit roster, register for events. Cannot transfer ownership.',
  head_coach: "Listed as the team's head coach. Can edit roster and register for events.",
  assistant_coach: 'Listed as assistant coach. Can edit roster.',
  viewer: 'Read-only access to team roster and schedule.',
};

const phoneRegex = /^\+?1?[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;

const buildSchema = () =>
  z
    .object({
      invitee_email: z.string().trim().email({ message: 'Please enter a valid email' }).max(255),
      invitee_name: z.string().trim().max(100).optional().or(z.literal('')),
      invitee_phone: z.string().trim().optional().or(z.literal('')),
      role: z.string().min(1),
      is_primary_billing: z.boolean().optional(),
      message: z.string().trim().max(500).optional().or(z.literal('')),
      send_method: z.enum(['Email', 'SMS', 'Both']),
    })
    .superRefine((data, ctx) => {
      if ((data.send_method === 'SMS' || data.send_method === 'Both')) {
        if (!data.invitee_phone || !phoneRegex.test(data.invitee_phone)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['invitee_phone'],
            message: 'Valid US phone is required for SMS',
          });
        }
      }
    });

type FormValues = z.infer<ReturnType<typeof buildSchema>>;

export default function InviteMemberDialog({
  open,
  onOpenChange,
  teamId,
  teamName,
  callerIsOwner,
  onMemberInvited,
}: Props) {
  const roleOptions = callerIsOwner ? ownerRoles : managerRoles;
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [magicLink, setMagicLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [inviteeLabel, setInviteeLabel] = useState<string>('');

  const form = useForm<FormValues>({
    resolver: zodResolver(buildSchema()),
    defaultValues: {
      invitee_email: '',
      invitee_name: '',
      invitee_phone: '',
      role: 'team_manager',
      is_primary_billing: false,
      message: '',
      send_method: 'Email',
    },
  });

  const watchedRole = form.watch('role');
  const watchedBilling = form.watch('is_primary_billing');
  const watchedSendMethod = form.watch('send_method');
  const watchedEmail = form.watch('invitee_email');
  const watchedName = form.watch('invitee_name');

  const reset = () => {
    form.reset();
    setSubmitError(null);
    setMagicLink(null);
    setCopied(false);
    setInviteeLabel('');
  };

  const handleClose = () => {
    reset();
    onOpenChange(false);
  };

  const onSubmit = async (values: FormValues) => {
    setSubmitting(true);
    setSubmitError(null);

    try {
      const { data: response, error: invokeError } = await invokeEdgeFunction(
        'rle-invite-team-member',
        {
          body: {
            team_id: teamId,
            invitee_email: values.invitee_email.trim().toLowerCase(),
            invitee_name: values.invitee_name || null,
            invitee_phone: values.invitee_phone || null,
            role: values.role,
            is_primary_billing: values.is_primary_billing ?? false,
            message: values.message || null,
            send_method: values.send_method.toLowerCase(),
          },
        }
      );

      if (invokeError) {
        toast.error(invokeError.message ?? 'Failed to send invitation');
        setSubmitting(false);
        return;
      }

      if (response?.ok === false) {
        const code = response?.error?.code;
        const msg = response?.error?.message ?? 'Something went wrong';
        const ctx = response?.error?.context;

        switch (code) {
          case 'ALREADY_MEMBER':
            setSubmitError('This person is already on the team.');
            break;
          case 'UNAUTHORIZED_ROLE':
            setSubmitError("You don't have permission to invite at this role.");
            form.setValue('role', 'team_manager');
            break;
          case 'INVALID_EMAIL':
            form.setError('invitee_email', { type: 'manual', message: msg || 'Invalid email' });
            break;
          case 'INVALID_ROLE':
            form.setValue('role', 'team_manager');
            setSubmitError(msg || 'Invalid role for this invitation.');
            break;
          case 'VALIDATION_FAILED':
            if (ctx?.field) {
              form.setError(ctx.field as any, { type: 'manual', message: msg });
            } else {
              setSubmitError(msg);
            }
            break;
          default:
            toast.error(msg);
        }
        setSubmitting(false);
        return;
      }

      const data = response?.data ?? {};
      if (data.status === 'already_member') {
        toast.info(`${values.invitee_email} is already a member of this team`);
        onMemberInvited();
        handleClose();
        return;
      }

      toast.success(`Invitation sent to ${values.invitee_email}`);
      setInviteeLabel(values.invitee_name || values.invitee_email);
      if (data.magic_link) {
        setMagicLink(data.magic_link);
      } else {
        onMemberInvited();
        handleClose();
      }
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to send invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyLink = async () => {
    if (!magicLink) return;
    try {
      await navigator.clipboard.writeText(magicLink);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy link');
    }
  };

  const handleDone = () => {
    onMemberInvited();
    handleClose();
  };

  const smsRequired = watchedSendMethod === 'SMS' || watchedSendMethod === 'Both';
  const inviteeDisplayName = watchedName || watchedEmail || 'this person';

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose();
        else onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-[520px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Invite a team member</DialogTitle>
          <DialogDescription>Invite someone to {teamName}</DialogDescription>
        </DialogHeader>

        {magicLink ? (
          <div className="space-y-4">
            <div className="rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
              <p className="font-medium mb-2">
                Invitation created. Share this link with {inviteeLabel}:
              </p>
              <div className="flex gap-2 items-center">
                <Input value={magicLink} readOnly className="text-xs" />
                <Button type="button" size="sm" variant="outline" onClick={handleCopyLink}>
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs mt-2 text-blue-800">
                (Email delivery is not yet enabled — share manually for now)
              </p>
            </div>
            <DialogFooter>
              <Button onClick={handleDone}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invitee_email">Email *</Label>
              <Input
                id="invitee_email"
                type="email"
                disabled={submitting}
                {...form.register('invitee_email')}
                className={form.formState.errors.invitee_email ? 'border-destructive' : ''}
              />
              {form.formState.errors.invitee_email && (
                <p className="text-xs text-destructive">{form.formState.errors.invitee_email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="invitee_name">Name (optional)</Label>
              <Input id="invitee_name" disabled={submitting} {...form.register('invitee_name')} />
              {form.formState.errors.invitee_name && (
                <p className="text-xs text-destructive">{form.formState.errors.invitee_name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="invitee_phone">
                Phone {smsRequired ? '*' : '(optional)'}
              </Label>
              <Input
                id="invitee_phone"
                disabled={submitting}
                placeholder="555-555-5555"
                {...form.register('invitee_phone')}
                className={form.formState.errors.invitee_phone ? 'border-destructive' : ''}
              />
              {form.formState.errors.invitee_phone && (
                <p className="text-xs text-destructive">{form.formState.errors.invitee_phone.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Role *</Label>
              <Select
                value={watchedRole}
                onValueChange={(v) => form.setValue('role', v)}
                disabled={submitting}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{roleDescriptions[watchedRole]}</p>
            </div>

            {callerIsOwner && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="is_primary_billing"
                    checked={!!watchedBilling}
                    onCheckedChange={(c) => form.setValue('is_primary_billing', !!c)}
                    disabled={submitting}
                  />
                  <Label htmlFor="is_primary_billing" className="cursor-pointer">
                    Make primary billing contact
                  </Label>
                </div>
                {watchedBilling && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                    This will take billing responsibility away from the current billing contact
                    once {inviteeDisplayName} accepts.
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="message">Personal message (optional)</Label>
              <Textarea
                id="message"
                disabled={submitting}
                placeholder="Hey Jen — can you help manage team registrations this season?"
                maxLength={500}
                {...form.register('message')}
              />
            </div>

            <div className="space-y-2">
              <Label>Send via</Label>
              <div className="inline-flex rounded-md border bg-muted p-1">
                {(['Email', 'SMS', 'Both'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    disabled={submitting}
                    onClick={() => form.setValue('send_method', m)}
                    className={`px-3 py-1 text-sm rounded ${
                      watchedSendMethod === m
                        ? 'bg-background shadow-sm font-medium'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>

            {submitError && (
              <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded p-2">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{submitError}</span>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {submitting ? 'Sending...' : 'Send invitation'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
